import React, { useEffect, useRef, useState } from "react";
import { BottomBar, ButtonGroup, CardsRenderer, CardsWrapper, Booking, DateRange, InlineFilterSelect, Pagination, Searchbox, Toast } from "../../../../../../Components";
import apis from "../../../../../../Utils/apis";
import { get } from "../../../../../../Utils/axios";
import { isFlightApprovalReadyToBook } from "../../../../../../Utils/flightApproval";
import moment from "moment";
import { useClientSettings } from "../../../../../../Hooks/useClientSettings";

function FlightBookings() {
	const BOOKING_STATUS_MAP = {
		booked: new Set(["BOOKED"]),
		inProgress: new Set(["PENDING", "PROCESSING", "CONFIRMING"]),
		rejected: new Set(["REJECTED"]),
		failed: new Set(["FAILED", "CANCELLED", "REFUNDED", "ARCHIVED"]),
	};
	const { isEnabled } = useClientSettings();
	const PAGE_SIZE_OPTIONS = [10, 20, 50];
	const DATE_FIELD_OPTIONS = [
		{ label: "Travel Date", value: "travelDate" },
		{ label: "Booking Date", value: "bookingDate" },
	];
	const [BookingStatus, setBookingStatus] = useState({ label: "Booked", value: "booked" });
	const initial = {
		bookings: [],
		count: 0,
		pages: 0,
	};
	const [Query, setQuery] = useState({
		search: "",
		page: 1,
		pageSize: 10,
		dateField: "travelDate",
		dates: {
			startDate: moment().subtract(30, "days").valueOf(),
			endDate: moment().add(30, "days").valueOf(),
		},
	});
	const [Bookings, setBookings] = useState(initial);
	const [Loading, setLoading] = useState(true);
	const bookingOptions = [
		{ label: "Booked", value: "booked" },
		...(isEnabled("ORGANIZATION")
			? [
					{ label: "In Progress", value: "inProgress" },
					{ label: "Approved Requests", value: "ready" },
					{ label: "Rejected", value: "rejected" },
				]
			: []),
		{ label: "Failed", value: "failed" },
	];
	const serverItems = Bookings.bookings || [];
	const normalizeStatus = (status) => `${status || ""}`.trim().toUpperCase();
	const tabItems =
		BookingStatus.value === "ready"
			? serverItems.filter((booking) => isFlightApprovalReadyToBook(booking))
			: BookingStatus.value === "inProgress"
				? serverItems.filter((booking) => {
						const status = normalizeStatus(booking?.status);
						return BOOKING_STATUS_MAP.inProgress.has(status) || (status === "APPROVED" && !isFlightApprovalReadyToBook(booking));
					})
				: (BOOKING_STATUS_MAP[BookingStatus.value] && serverItems.filter((booking) => BOOKING_STATUS_MAP[BookingStatus.value].has(normalizeStatus(booking?.status)))) || serverItems;
	const hasStatusMismatch = tabItems.length !== serverItems.length;
	const hasServerCount = Number.isFinite(Number(Bookings.count)) && Number(Bookings.count) > 0;
	const totalCount = hasStatusMismatch ? tabItems.length : hasServerCount ? Number(Bookings.count) : tabItems.length;
	const totalPages = hasStatusMismatch
		? Math.max(1, Math.ceil(totalCount / Query.pageSize))
		: Number.isFinite(Number(Bookings.pages)) && Number(Bookings.pages) > 0
			? Number(Bookings.pages)
			: Math.max(1, Math.ceil(totalCount / Query.pageSize));
	const safePage = Math.min(Math.max(Query.page, 1), totalPages);
	const needsClientWindow = hasStatusMismatch || tabItems.length > Query.pageSize;
	const visibleBookings = needsClientWindow ? tabItems.slice((safePage - 1) * Query.pageSize, safePage * Query.pageSize) : tabItems;
	const currentItemsCount = visibleBookings.length;
	const listTopRef = useRef(null);
	const pageStart = currentItemsCount ? (safePage - 1) * Query.pageSize + 1 : 0;
	const pageEnd = currentItemsCount ? pageStart + currentItemsCount - 1 : 0;

	useEffect(() => {
		fetchBookings(BookingStatus.value, Query);
	}, [BookingStatus, Query]);

	useEffect(() => {
		if (listTopRef.current) {
			listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, [Query.page]);

	useEffect(() => {
		if (Query.page > totalPages) {
			setQuery((prev) => ({ ...prev, page: totalPages }));
		}
	}, [Query.page, totalPages]);

	const fetchBookings = (type, query) => {
		setLoading(true);
		let url;
		if (type === "inProgress" || type === "ready") {
			url = apis.getPendingFlightBookings;
		} else if (type === "booked") {
			url = apis.getBookedFlightBookings;
		} else if (type === "rejected") {
			url = apis.getRejectedFlightBookings;
		} else if (type === "failed") {
			url = apis.getArchivedFlightBookings;
		}
		get(
			url,
			{
				...query,
				// The bookings API already supports dateField, so the UI only needs to pass through the selected mode.
				dates: {
					startDate: moment(query.dates.startDate).toDate(),
					endDate: moment(query.dates.endDate).toDate(),
				},
			},
			(r, e) => {
				if (r) {
					setBookings(r);
					setLoading(false);
				} else if (e) {
					Toast.handleError(e);
					setLoading(false);
				}
			}
		);
	};

	return (
		<>
			<BottomBar>
				<div className="flex flex-col w-full gap-1.5">
					<div className="flex items-end justify-between gap-2">
						<h5 className="leading-tight">{totalCount} Flight Bookings</h5>
						<div className="text-xs text-secondary whitespace-nowrap">
							Showing {pageStart}-{pageEnd}
							{hasServerCount ? ` of ${totalCount}` : ""}
						</div>
					</div>
					<div className="flex items-center justify-between gap-2">
						<Pagination showNumbers={false} active={safePage} total={totalPages} onChange={(page) => setQuery({ ...Query, page })} />
						<label className="flex items-center text-xs text-secondary whitespace-nowrap gap-1">
							<span>Page size</span>
							<select
								value={Query.pageSize}
								onChange={(e) =>
									setQuery((prev) => ({
										...prev,
										page: 1,
										pageSize: Number(e.target.value),
									}))
								}
								className="border rounded-md p-1.5 text-xs bg-canvas"
							>
								{PAGE_SIZE_OPTIONS.map((size) => (
									<option key={size} value={size}>
										{size}
									</option>
								))}
							</select>
						</label>
					</div>
				</div>
			</BottomBar>
			<div ref={listTopRef} className="mt-6 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
				<div className="w-full xl:w-auto">
					<div className="grid grid-cols-2 gap-2 sm:hidden">
						{bookingOptions.map((option) => {
							const isActive = BookingStatus?.value === option.value;
							return (
								<button
									key={option.value}
									type="button"
									onClick={() => {
										setBookingStatus(option);
										setQuery((prev) => ({ ...prev, page: 1 }));
									}}
									className={`btn btn-xs ${isActive ? "btn-secondary" : "btn-outlined"} w-full justify-center !px-3 !py-2 text-center`}
								>
									{option.label}
								</button>
							);
						})}
					</div>
					<div className="hidden w-full sm:block xl:w-auto">
						<ButtonGroup
							onClick={(status) => {
								setBookingStatus(status);
								setQuery((prev) => ({ ...prev, page: 1 }));
							}}
							size="xs !p-1.5"
							selected={BookingStatus}
							className="w-full text-sm"
							options={bookingOptions}
						/>
					</div>
				</div>
				<div className="flex w-full flex-col gap-2 lg:flex-row lg:items-center lg:gap-x-2 xl:w-auto">
					<Searchbox className="w-full lg:flex-1 xl:w-96" placeholder="Search by Ref no, airport code, airline code, flight no" cmd="s" onChange={(search) => setQuery({ ...Query, search, page: 1 })} />
					<div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] lg:w-[26rem] xl:w-auto">
						<InlineFilterSelect
							value={Query.dateField}
							options={DATE_FIELD_OPTIONS}
							onChange={(option) =>
								setQuery((prev) => ({
									...prev,
									page: 1,
									dateField: option.value,
								}))
							}
						/>
						<DateRange className="w-full" backDateSelection={true} setRange={(dates) => setQuery({ ...Query, dates, page: 1 })} range={Query.dates} inputClass="!p-1.5 !text-xs w-full" />
					</div>
				</div>
			</div>
			<CardsWrapper loading={Loading}>
				<CardsRenderer
					items={visibleBookings}
					render={(booking, i) => {
						return <Booking key={i} booking={booking} type="flight" onReload={() => fetchBookings(BookingStatus.value, Query)} />;
					}}
				/>
			</CardsWrapper>
		</>
	);
}

export default FlightBookings;
